import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import ArrowBack from '@mui/icons-material/ArrowBack'
import Reply from '@mui/icons-material/Reply'
import type { ConversationTurnEntry } from '@/features/events/conversation/state/types'
import type { FormattedUserActivity } from '@/features/events/shared'
import { EventType, resolveEventTypeColorFor } from '@/index'
import { claudeApiErrorFrom, isClaudeApiError } from '@/features/events/conversation/models'
import { ERRORED_TOOL_NAME } from '@/infra/sse/conversation/decryption/resolveNotificationContext'
import { formatMcpToolForDisplay, formatToolNameForDisplay } from '@/features/events/shared/utils'
import { standardTooltipProps } from '@/common/components'
import {
  getIconRotation,
  resolveEventTypeIconFor,
  shouldRotateIcon,
} from '@/features/events/shared/types/enums/EventType'
import { truncateAtWordBoundary } from '@/features/events/shared/utils/textTruncationUtils'

const USER_REPLY_MAX_LENGTH = 60

interface MostRecentEventPreviewProps {
  readonly event: ConversationTurnEntry
  readonly isProcessing: boolean
  readonly onClick: () => void
  readonly userActivity?: FormattedUserActivity | null
  readonly userReplyText?: string | null
}

export const MostRecentEventPreview: FC<MostRecentEventPreviewProps> = ({
  event,
  isProcessing,
  onClick,
  userActivity,
  userReplyText,
}) => {
  const theme = useTheme()

  const isApiError =
    event.eventType === EventType.AGENT_REPLY && isClaudeApiError(event.agentResponse)
  const isErroredEvent = event.eventType === EventType.ERRORED_EVENT || isApiError
  const eventColor = isApiError
    ? theme.palette.error.main
    : resolveEventTypeColorFor(event.eventType, theme)
  const shouldPulse = shouldEventTypePulse(event.eventType, isProcessing)

  if (isErroredEvent && isApiError && event.agentResponse) {
    return renderApiError(event.agentResponse, eventColor, shouldPulse, onClick)
  }

  if (isErroredEvent && event.eventType === EventType.ERRORED_EVENT) {
    return renderErroredEvent(event, eventColor, shouldPulse, onClick)
  }

  const isAcknowledged = userActivity?.isBlockingResponse && userActivity?.isAcknowledged
  const isUserReplyPending =
    userActivity?.isBlockingResponse && !userActivity?.isAcknowledged && !!userReplyText

  if (isUserReplyPending) {
    return renderUserReplyPending(theme, userReplyText)
  }

  if (isAcknowledged && userActivity?.pendingAcknowledgement) {
    return renderAcknowledgedState(theme, userActivity.pendingAcknowledgement, onClick)
  }

  if (event.eventType === EventType.FILE_MODIFICATION && event.notificationMessage) {
    return renderFileModification(event, eventColor, shouldPulse, onClick, theme)
  }

  if (event.eventType === EventType.AGENT_REPLY) {
    return renderAgentReply(event, event.agentResponse ?? '', theme, onClick)
  }

  return renderDefaultPreview(event, eventColor, shouldPulse, onClick)
}

function renderUserReplyPending(theme: Theme, userReplyText: string) {
  const displayText = truncateAtWordBoundary(userReplyText, USER_REPLY_MAX_LENGTH)
  return (
    <Box sx={nonClickablePreviewRowSx}>
      <Box sx={iconColumnSx}>
        <ArrowBack sx={pulsingIconSx(theme.palette.success.main)} />
      </Box>
      <Typography variant="body2" sx={nonClickableMessageSx}>
        {displayText}
      </Typography>
    </Box>
  )
}

function renderAcknowledgedState(
  theme: Theme,
  pendingAcknowledgement: { eventType: EventType; toolName: string | null },
  onClick: () => void,
) {
  const message = buildAcknowledgementMessage(
    pendingAcknowledgement.eventType,
    pendingAcknowledgement.toolName,
  )
  return (
    <Box sx={previewRowSx} onClick={onClick}>
      <Box sx={iconColumnSx}>
        <EventTypeIcon
          eventType={pendingAcknowledgement.eventType}
          shouldPulse={false}
          colorOverride={theme.palette.success.main}
        />
      </Box>
      <Tooltip {...standardTooltipProps} title="View event details" followCursor>
        <Typography variant="body2" sx={clickableMessageSx}>
          {message}
        </Typography>
      </Tooltip>
    </Box>
  )
}

function renderApiError(
  agentResponse: string,
  eventColor: string,
  shouldPulse: boolean,
  onClick: () => void,
) {
  const apiError = claudeApiErrorFrom(agentResponse)
  const errorMessage = apiError?.message ?? 'Unknown error'

  return (
    <Box sx={previewRowSx} onClick={onClick}>
      <Box sx={iconColumnSx}>
        <EventTypeIcon
          eventType={EventType.ERRORED_EVENT}
          shouldPulse={shouldPulse}
          colorOverride={eventColor}
        />
      </Box>
      <Tooltip {...standardTooltipProps} title="View error details" followCursor>
        <Typography variant="body2" sx={apiErrorMessageSx}>
          Claude API Error: {errorMessage}
        </Typography>
      </Tooltip>
    </Box>
  )
}

function renderErroredEvent(
  event: ConversationTurnEntry,
  eventColor: string,
  shouldPulse: boolean,
  onClick: () => void,
) {
  const errorMessage = resolveErrorMessage(event)

  return (
    <Box sx={previewRowSx} onClick={onClick}>
      <Box sx={iconColumnSx}>
        <EventTypeIcon
          eventType={event.eventType}
          shouldPulse={shouldPulse}
          colorOverride={eventColor}
        />
      </Box>
      <Tooltip {...standardTooltipProps} title="View error details" followCursor>
        <Typography variant="body2" sx={clickableMessageSx}>
          {errorMessage}
        </Typography>
      </Tooltip>
    </Box>
  )
}

function renderFileModification(
  event: ConversationTurnEntry,
  eventColor: string,
  shouldPulse: boolean,
  onClick: () => void,
  theme: Theme,
) {
  const message = event.notificationMessage ?? ''
  const colonIndex = message.lastIndexOf(': ')
  const fileName = colonIndex === -1 ? message : message.substring(colonIndex + 2)

  const rawToolName = event.notificationContext?.toolName ?? 'Edit'
  const toolName = formatToolNameForDisplay(rawToolName)

  return (
    <Box sx={previewRowSx} onClick={onClick}>
      <Box sx={iconColumnSx}>
        <EventTypeIcon
          eventType={event.eventType}
          shouldPulse={shouldPulse}
          colorOverride={eventColor}
        />
      </Box>
      <Tooltip {...standardTooltipProps} title="View event details" followCursor>
        <Box sx={clickableContainerSx}>
          <Typography component="span" variant="body2" sx={messageSx}>
            Claude Code used{' '}
          </Typography>
          <Typography component="span" variant="body2" sx={fileNameHighlightSx(theme)}>
            {toolName}
          </Typography>
          <Typography component="span" variant="body2" sx={messageSx}>
            {' '}
            tool:{' '}
          </Typography>
          <Typography component="span" variant="body2" sx={fileNameHighlightSx(theme)}>
            {fileName}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  )
}

function renderAgentReply(
  _event: ConversationTurnEntry,
  agentResponse: string,
  theme: Theme,
  onClick: () => void,
) {
  return (
    <Box sx={previewRowSx} onClick={onClick}>
      <Box sx={iconColumnSx}>
        <Reply sx={staticIconSx(theme.palette.success.main)} />
      </Box>
      <Tooltip {...standardTooltipProps} title="View event details" followCursor>
        <Typography variant="body2" sx={clickableMessageSx}>
          {formatMostRecentEventPreview(agentResponse)}
        </Typography>
      </Tooltip>
    </Box>
  )
}

function renderDefaultPreview(
  event: ConversationTurnEntry,
  eventColor: string,
  shouldPulse: boolean,
  onClick: () => void,
) {
  const isAgentNarrative = event.eventType === EventType.AGENT_NARRATIVE
  const isUserPrompt = event.eventType === EventType.USER_PROMPT
  const eventContent = resolveEventContent(event, isAgentNarrative, isUserPrompt)
  const displayContent =
    isAgentNarrative || isUserPrompt ? formatMostRecentEventPreview(eventContent) : eventContent

  return (
    <Box sx={previewRowSx} onClick={onClick}>
      <Box sx={iconColumnSx}>
        <EventTypeIcon
          eventType={event.eventType}
          shouldPulse={shouldPulse}
          colorOverride={eventColor}
        />
      </Box>
      <Tooltip {...standardTooltipProps} title="View event details" followCursor>
        <Typography variant="body2" sx={clickableMessageSx}>
          {displayContent}
        </Typography>
      </Tooltip>
    </Box>
  )
}

interface EventTypeIconProps {
  readonly eventType: EventType
  readonly shouldPulse?: boolean
  readonly colorOverride?: string
}

const EventTypeIcon: FC<EventTypeIconProps> = ({
  eventType,
  shouldPulse = false,
  colorOverride,
}) => {
  const theme = useTheme()

  const Icon = resolveEventTypeIconFor(eventType)
  if (!Icon) return null

  const color = colorOverride ?? resolveEventTypeColorFor(eventType, theme)
  const rotate = shouldRotateIcon(eventType)
  const rotationAngle = getIconRotation(eventType)
  const iconStyle = shouldPulse ? pulsingIconSx(color) : staticIconSx(color)

  if (rotate) {
    return (
      <Box sx={rotatedIconContainerSx(rotationAngle)}>
        <Icon sx={iconStyle} />
      </Box>
    )
  }

  return <Icon sx={iconStyle} />
}

function shouldEventTypePulse(eventType: EventType, isProcessing: boolean): boolean {
  if (!isProcessing) return false

  switch (eventType) {
    case EventType.FILE_MODIFICATION:
    case EventType.AGENT_REPLY:
      return false
    default:
      return true
  }
}

function buildAcknowledgementMessage(eventType: EventType, toolName: string | null): string {
  switch (eventType) {
    case EventType.TOOL_PERMISSION_REQUEST: {
      if (!toolName) return 'Tool request acknowledged'
      const mcpFormatted = formatMcpToolForDisplay(toolName)
      const displayName = mcpFormatted ? mcpFormatted.toolDisplayName : toolName
      return `${displayName} request acknowledged`
    }
    case EventType.PLAN_APPROVAL_REQUEST:
      return 'Plan approval request acknowledged'
    case EventType.AGENT_QUESTIONS:
      return 'User answers acknowledged'
    default:
      return 'Request acknowledged'
  }
}

function resolveEventContent(
  event: ConversationTurnEntry,
  isAgentNarrative: boolean,
  isUserPrompt: boolean,
): string {
  if (isAgentNarrative) return event.agentResponse || ''
  if (isUserPrompt) return event.userPrompt || ''
  return event.notificationMessage || ''
}

function resolveErrorMessage(event: ConversationTurnEntry): string {
  const ctx = event.notificationContext

  if (ctx && ctx.toolName !== ERRORED_TOOL_NAME && event.notificationMessage) {
    return event.notificationMessage
  }

  if (!ctx) return 'Failed to deliver agent request'

  if (ctx.toolName === ERRORED_TOOL_NAME) {
    const toolInput = ctx.toolInput as { error?: string } | undefined
    return toolInput?.error ?? 'Failed to deliver agent request'
  }

  return 'Failed to deliver agent request'
}

const PREVIEW_MAX_LENGTH = 80

function formatMostRecentEventPreview(text: string, maxLength = PREVIEW_MAX_LENGTH): string {
  const stripped = stripMarkdownSyntax(text)
  const normalized = stripped.replaceAll(/\s+/g, ' ').trim()
  return truncateAtWordBoundary(normalized, maxLength)
}

function stripMarkdownSyntax(text: string): string {
  return text
    .replaceAll(/```[\s\S]*?```/g, '')
    .replaceAll(/`([^`]+)`/g, '$1')
    .replaceAll(/!\[([^]]*)]\([^)]+\)/g, '$1')
    .replaceAll(/\[([^]]+)]\([^)]+\)/g, '$1')
    .replaceAll(/(\*\*|__)(.*?)\1/g, '$2')
    .replaceAll(/([*_])([^*_]+)\1/g, '$2')
    .replaceAll(/~~(.*?)~~/g, '$1')
    .replaceAll(/^#{1,6}\s+/gm, '')
    .replaceAll(/^>\s+/gm, '')
    .replaceAll(/^(-{3,}|\*{3,}|_{3,})$/gm, '')
    .replaceAll(/^\s*[-*+]\s+/gm, '')
    .replaceAll(/^\s*\d+\.\s+/gm, '')
    .replaceAll(/\n+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim()
}

const previewRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  cursor: 'pointer',
}

export const nonClickablePreviewRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

export const iconColumnSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
}

export const staticIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '1rem',
  color,
  fontWeight: 'bold',
  display: 'block',
})

const pulsingIconSx = (color: string): SxProps<Theme> => ({
  fontSize: '1rem',
  color,
  fontWeight: 'bold',
  display: 'block',
  animation: 'eventIconPulse 2s ease-in-out infinite',
  '@keyframes eventIconPulse': {
    '0%': { opacity: 0.6, transform: 'scale(1)' },
    '50%': { opacity: 1, transform: 'scale(1.15)' },
    '100%': { opacity: 0.6, transform: 'scale(1)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
})

const rotatedIconContainerSx = (angle: number): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: `rotate(${angle}deg)`,
})

const messageSx: SxProps<Theme> = {
  fontWeight: 400,
  fontSize: '0.875rem',
  color: 'text.primary',
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const clickableMessageSx: SxProps<Theme> = {
  ...messageSx,
  cursor: 'pointer',
  transition: 'color 0.2s',
  '&:hover': {
    color: 'primary.main',
  },
}

export const nonClickableMessageSx: SxProps<Theme> = {
  ...messageSx,
}

const clickableContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
  '&:hover': {
    '& .MuiTypography-root': {
      color: 'primary.main',
      transition: 'color 0.2s',
    },
  },
}

const apiErrorMessageSx: SxProps<Theme> = {
  fontWeight: 400,
  fontSize: '0.875rem',
  color: 'text.primary',
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'color 0.2s',
  '&:hover': {
    color: 'primary.main',
  },
}

const fileNameHighlightSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  fontSize: 'inherit',
})
